var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useCallback, useReducer, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { apiGetOneRfpOffer } from '@/actions/rfpProOfferActions';
import { CustomNavigate } from '@/components/functional/Link/CustomNavigate';
import { CustomRoute } from '@/components/functional/Link/CustomRoute';
import { CustomRoutes } from '@/components/functional/Link/CustomRoutes';
import { notify } from '@/helpers/action';
import { useNavigate } from '@/hooks/lib/react-router/useNavigate';
import { useRfpOneProfessional } from '@/hooks/pages/rfp/professional/useRfpOneProfessional';
import { useTypeSelector } from '@/hooks/state/useTypeSelector';
import { RfpProOffer } from '@/pages/rfp/professional/offer/RfpProOffer';
import { RfpProOfferComplete } from '@/pages/rfp/professional/offer/RfpProOfferComplete';
import { RfpProOfferConfirm } from '@/pages/rfp/professional/offer/RfpProOfferConfirm';
import { RfpProOfferPayment } from '@/pages/rfp/professional/offer/RfpProOfferPayment';
import { reducer, replaceRfpProOfferFormAction, } from '@/pages/rfp/professional/offer/RfpProOfferRoot/RfpProOfferRootReducer';
import { useRfpOfferLock } from '@/pages/rfp/professional/offer/RfpProOfferRoot/useRfpOfferLock';
import { useRfpOfferStep } from '@/pages/rfp/professional/offer/RfpProOfferRoot/useRfpOfferStep';
import { RfpProOfferSkeleton } from '@/pages/rfp/professional/offer/RfpProOfferSkeleton';
import { isAxiosError } from '@/types/api/apiErrorTypes';
import { wrapDispatch } from '@/utils/actions/dispatch';
import { BAD_RFP_OFFER_UPDATING } from '@/utils/constants/api/responseCode';
import { RFP_PROFESSIONAL_DATA_TYPE_UNAPPROVED } from '@/utils/constants/rfp/professional/dataType';
import { RFP_OFFER_MEMBER_STATUS_OFFER_MEMBER } from '@/utils/constants/rfp/rfpOfferMember/status';
import { VIEW_REQUEST_PROPOSAL_TODO } from '@/utils/constants/viewRequest/proposal';
import { pagePath, getChildPath } from '@/utils/router/pagePath';
const rootPath = pagePath.project.professional.offerRoot(':id');
const professionalPath = pagePath.project.professional;
/**
 * 提案提出 ルーティング
 */
export const RfpProOfferRoot = () => {
    const history = useNavigate();
    const { id: paramId = '' } = useParams();
    /** 提案提出データ */
    const offer = useTypeSelector((state) => state.professionalRfpOffer.one[paramId] || null);
    // ロックIDの管理
    const { lockId } = useRfpOfferLock();
    /** ステップの管理 */
    const { changeStepValue, validStep } = useRfpOfferStep();
    /** Form の値管理 */
    const [formState, formDispatch] = useReducer(reducer, null);
    /**
     * 案件提案提出データ 取得
     */
    const fetchOffer = useCallback((dispatch, { id }, unmountRef) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        try {
            const action = apiGetOneRfpOffer({ path: { id, lock_id: lockId } }, {});
            const { payload } = yield wrapDispatch(dispatch, action);
            if (!unmountRef.current) {
                const teamMembers = (payload.rfp_offer_members || []).filter((member) => member.status === RFP_OFFER_MEMBER_STATUS_OFFER_MEMBER.value);
                const data = Object.assign(Object.assign({}, payload), { rfp_offer_members: teamMembers });
                formDispatch(replaceRfpProOfferFormAction(data));
            }
        }
        catch (error) {
            if (unmountRef.current || !isAxiosError(error)) {
                return;
            }
            const errorCode = (_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.code;
            // 他のユーザーが編集中の場合
            if (errorCode === BAD_RFP_OFFER_UPDATING) {
                notify('他のユーザーが編集中です');
                history.push(professionalPath.detail(id));
            }
        }
    }), [history, lockId]);
    // 案件詳細データ取得, ページ状態管理
    const { id, loading, rfp } = useRfpOneProfessional(!formState
        ? {
            afterCallbackInApproved: fetchOffer,
        }
        : {});
    /** 所定通りのページ遷移が行われなかった場合のリダイレクト設定 */
    const invalidStepNavigateTo = useMemo(() => ({
        pathname: professionalPath.offer(id),
        state: { lockId },
    }), [id, lockId]);
    // リダイレクト：案件が存在しない場合
    if ((!loading && !rfp) || (!loading && !offer)) {
        return _jsx(CustomNavigate, { to: professionalPath.list() });
    }
    // ローディング表示
    if (!formState || (loading && !rfp) || (loading && !offer)) {
        return _jsx(RfpProOfferSkeleton, {});
    }
    // リダイレクト: 案件詳細 未承認
    if (rfp.data_type === RFP_PROFESSIONAL_DATA_TYPE_UNAPPROVED.value) {
        return _jsx(CustomNavigate, { to: professionalPath.detail(id) });
    }
    // リダイレクト：提案不可能の場合(提出済 or 提案意向なし)
    if (rfp.has_offer || rfp.proposal !== VIEW_REQUEST_PROPOSAL_TODO.value) {
        return _jsx(CustomNavigate, { to: professionalPath.detail(id) });
    }
    // リダイレクト：案件提案提出データが存在しない場合
    if (!loading && !offer) {
        return _jsx(CustomNavigate, { to: professionalPath.detail(id) });
    }
    // リダイレクト: 所定通りのページ遷移が行われなかった場合
    if (!validStep) {
        return _jsx(CustomNavigate, { to: invalidStepNavigateTo });
    }
    return (_jsxs(CustomRoutes, { children: [_jsx(CustomRoute, { element: _jsx(RfpProOffer, { changeStepValue: changeStepValue, formDispatch: formDispatch, formValues: formState, lockId: lockId, rfp: rfp }), path: getChildPath(professionalPath.offer(':id'), rootPath) }), _jsx(CustomRoute, { element: _jsx(RfpProOfferPayment, { changeStepValue: changeStepValue, formDispatch: formDispatch, formValues: formState, lockId: lockId, rfp: rfp }), path: getChildPath(professionalPath.offerPayment(':id'), rootPath) }), _jsx(CustomRoute, { element: _jsx(RfpProOfferConfirm, { changeStepValue: changeStepValue, formValues: formState, lockId: lockId, rfp: rfp }), path: getChildPath(professionalPath.offerConfirm(':id'), rootPath) }), _jsx(CustomRoute, { element: _jsx(RfpProOfferComplete, { rfp: rfp }), path: getChildPath(professionalPath.offerComplete(':id'), rootPath) })] }));
};
